import { Component, computed, inject, input, output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { EntityInterface } from '@core/models/entities/entity-visualization-interface';
import { EntityIconService } from '@core/services/entity-icon/entity-icon.service';
import { ICONS } from '@core/services/icons/icons.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { ReferenceFieldOption } from '@core/services/reference-fields/reference-fields.service';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { DropdownDirective } from '@shared/directives/dropdown.directive';
import { IncludeInFilterStatus } from '@shared/utils/include-in-filter.utils';
import { getNestedValue } from '@shared/utils/table-cell.utils';

interface ReferenceFieldRow {
  key: string;
  label: string;
  value: unknown;
}

/**
 * Dumb popover content for a reference cell: resolved name, uid, a
 * user-selected set of extra fields, and quick actions. Holds no local copy
 * of persisted/resolved state — every displayed value is pushed in via
 * inputs, and every user intent goes out as an output.
 */
@Component({
  selector: 'app-reference-detail-popover',
  imports: [TranslatePipe, CheckboxComponent, DropdownDirective],
  templateUrl: './reference-detail-popover.component.html',
  styles: `
    :host {
      display: contents;
    }
  `
})
export class ReferenceDetailPopoverComponent {
  private readonly translate = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);
  private readonly entityIconService = inject(EntityIconService);
  protected readonly ICONS = ICONS;

  name = input<string | undefined>(undefined);
  uid = input.required<string>();
  targetEntityType = input.required<string>();
  availableFields = input<ReferenceFieldOption[]>([]);
  selectedFields = input<string[]>([]);
  record = input<EntityInterface<unknown> | undefined>(undefined);
  loading = input<boolean>(false);
  error = input<boolean>(false);
  /** Off where there is nothing to filter — a findings panel is not a table. */
  canAddToFilter = input<boolean>(true);
  includeInFilterStatus = input<IncludeInFilterStatus>('hidden');

  changeFields = output<string[]>();
  addToFilter = output<void>();
  includeInFilter = output<void>();
  dismiss = output<void>();

  protected readonly entityIcon = computed(() =>
    this.entityIconService.getEntityIcon(this.targetEntityType())
  );

  protected readonly fieldRows = computed<ReferenceFieldRow[]>(() => {
    const labelByKey = new Map(this.availableFields().map((f) => [f.key, f.label]));
    const record = this.record();
    return this.selectedFields().map((key) => ({
      key,
      label: labelByKey.get(key) ?? key,
      value: record ? getNestedValue(record, key) : undefined
    }));
  });

  protected fieldToggleHandler(key: string): (checked: boolean) => void {
    return (checked: boolean) => this.onToggleField(key, checked);
  }

  protected formatFieldValue(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  protected copyUid(): void {
    navigator.clipboard.writeText(this.uid()).then(
      () => {
        this.notificationService.success(
          this.translate.instant('table.cell.overlay_actions.copy.message_success'),
          '',
          2000
        );
      },
      () => {
        this.notificationService.error(
          this.translate.instant('table.cell.overlay_actions.copy.message_error'),
          ''
        );
      }
    );
  }

  private onToggleField(key: string, checked: boolean): void {
    const current = this.selectedFields();
    const next = checked ? [...current, key] : current.filter((k) => k !== key);
    this.changeFields.emit(next);
  }
}
